import { defaultFrameName, defaultLayerName } from './names.js';
import type { Component, DesignDocument, Frame, ImageLayer, Layer, LayerId } from './types.js';

/**
 * Reading a design.
 *
 * A hand-written parser rather than `DOMParser`, for two reasons that are not
 * about performance. It has to run in Node — the CLI reads designs, and the
 * linter runs in CI — and it has to be *strict*: a browser parser is
 * specified to recover from anything, which is exactly wrong for a format whose
 * value is that an unknown construct is reported rather than silently dropped.
 *
 * The grammar is deliberately tiny. Four element names, five attributes, no
 * self-closing forms except `<image>`, no entities beyond the five XML ones, no
 * comments, no processing instructions. Anything else is an error naming the
 * line it is on.
 */

export interface ParseError {
  readonly line: number;
  readonly message: string;
}

export type ParseResult =
  | { readonly ok: true; readonly design: DesignDocument }
  | { readonly ok: false; readonly errors: readonly ParseError[] };

interface Token {
  readonly kind: 'open' | 'close' | 'text';
  readonly name: string;
  readonly attrs: Record<string, string>;
  readonly text: string;
  readonly line: number;
  readonly selfClosing: boolean;
}

const ELEMENTS = new Set(['design', 'frame', 'box', 'text', 'image', 'define', 'use']);

/**
 * A `<use>`'s own attributes. Everything else it carries is a slot value.
 *
 * An open set is right here and wrong everywhere else in this format: the slot
 * names come from the definition, and the *linter* checks them against it. The
 * parser cannot, because a use may appear before the define it names — and a
 * parser that refused a forward reference would make file order load-bearing.
 */
const USE_ATTRS = new Set(['id', 'name', 'class', 'component']);

/** The five XML entities, and nothing else — an unknown one is an error. */
const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

/**
 * Decode entities, and never throw.
 *
 * `String.fromCodePoint` raises `RangeError` for anything above U+10FFFF, and
 * this function runs inside a React render *and* inside a ProseMirror
 * `decorations()` call — so one `&#1114112;` in a design took down the canvas
 * and every prose document that linked to it. A parser whose entire job is to
 * refuse rather than crash must not be the thing that crashes.
 *
 * Hex entities are accepted too. They were not, and the failure was quiet in
 * the worst way: `&#x27;` matched nothing, so no error was raised *and* the
 * literal text survived to be escaped into `&amp;#x27;` on the way out — a
 * round trip that changed what a label said.
 */
function decode(value: string, line: number, errors: ParseError[]): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, entity: string) => {
    if (entity.startsWith('#')) {
      const point = entity[1]?.toLowerCase() === 'x' ? Number.parseInt(entity.slice(2), 16) : Number(entity.slice(1));
      // Surrogates are excluded as well as out-of-range values: a lone
      // surrogate is a legal code point and an illegal character, and letting
      // one through produces a string that cannot be serialized.
      if (!Number.isInteger(point) || point < 0 || point > 0x10ffff || (point >= 0xd800 && point <= 0xdfff)) {
        errors.push({ line, message: `\`${whole}\` is not a character.` });
        return whole;
      }
      return String.fromCodePoint(point);
    }
    const decoded = ENTITIES[entity.toLowerCase()];
    if (decoded === undefined) {
      errors.push({ line, message: `\`${whole}\` is not an entity this format knows.` });
      return whole;
    }
    return decoded;
  });
}

export function encode(value: string): string {
  return value.replace(/[&<>]/g, (character) => `&${{ '&': 'amp', '<': 'lt', '>': 'gt' }[character]};`);
}

function encodeAttribute(value: string): string {
  return encode(value).replace(/"/g, '&quot;');
}

/**
 * Where a tag ends, respecting quoted attribute values.
 *
 * `indexOf('>')` is wrong and wrong *silently*: `<text name="a>b">hi</text>`
 * ended the tag inside the quotes, so the name was destroyed, the leftover
 * `b">hi` became the element's content, and the parse reported success. Silent
 * corruption is the one outcome this format is built to make impossible.
 */
function tagEnd(source: string, from: number): number {
  let quoted = false;
  for (let i = from + 1; i < source.length; i++) {
    const character = source[i];
    if (character === '"') quoted = !quoted;
    else if (character === '>' && !quoted) return i;
    // An unterminated quote cannot run past the line the tag started on —
    // otherwise one missing `"` swallows the rest of the document.
    else if (character === '\n' && quoted) return -1;
  }
  return -1;
}

function tokenize(source: string, errors: ParseError[]): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  let line = 1;

  const advance = (to: number): void => {
    for (let i = index; i < to; i++) if (source[i] === '\n') line++;
    index = to;
  };

  while (index < source.length) {
    const next = source.indexOf('<', index);
    if (next === -1) {
      const text = source.slice(index);
      if (text.trim()) tokens.push({ kind: 'text', name: '', attrs: {}, text: decode(text, line, errors), line, selfClosing: false });
      break;
    }
    if (next > index) {
      const text = source.slice(index, next);
      if (text.trim()) {
        tokens.push({ kind: 'text', name: '', attrs: {}, text: decode(text, line, errors), line, selfClosing: false });
      }
      advance(next);
    }

    const end = tagEnd(source, index);
    if (end === -1) {
      errors.push({ line, message: 'A tag was opened and never closed.' });
      break;
    }
    const raw = source.slice(index + 1, end);
    const startLine = line;

    if (raw.startsWith('/')) {
      tokens.push({ kind: 'close', name: raw.slice(1).trim().toLowerCase(), attrs: {}, text: '', line: startLine, selfClosing: false });
      advance(end + 1);
      continue;
    }

    const selfClosing = raw.endsWith('/');
    const body = selfClosing ? raw.slice(0, -1) : raw;
    const nameMatch = /^([a-z]+)/i.exec(body.trim());
    if (!nameMatch) {
      errors.push({ line: startLine, message: 'A tag with no element name.' });
      advance(end + 1);
      continue;
    }
    const name = nameMatch[1]!.toLowerCase();
    // A null-prototype object, so an attribute called `constructor` or
    // `toString` is an ordinary key rather than something already "present".
    const attrs: Record<string, string> = Object.create(null) as Record<string, string>;
    const rest = body.trim().slice(nameMatch[1]!.length);
    // Consume the attribute list token by token rather than scanning for the
    // ones that look right. Skipping what does not match is how `class=flex`
    // lost a layer's entire styling with no error at all.
    const attributePattern = /\s*([a-z-]+)(\s*=\s*(?:"([^"]*)"|([^\s"'>]+))?)?/giy;
    attributePattern.lastIndex = 0;
    let consumed = 0;
    let match: RegExpExecArray | null;
    while ((match = attributePattern.exec(rest)) !== null) {
      consumed = attributePattern.lastIndex;
      const key = match[1]!.toLowerCase();
      if (match[3] === undefined) {
        errors.push({
          line: startLine,
          message:
            match[4] !== undefined
              ? `\`${key}\` needs quotes around its value: \`${key}="${match[4]}"\`.`
              : `\`${key}\` has no value.`,
        });
        continue;
      }
      if (key in attrs) {
        errors.push({ line: startLine, message: `\`${key}\` is given twice on one \`<${name}>\`.` });
        continue;
      }
      attrs[key] = decode(match[3], startLine, errors);
    }
    if (consumed < rest.trimEnd().length) {
      errors.push({ line: startLine, message: `\`${rest.slice(consumed).trim()}\` is not an attribute.` });
    }
    tokens.push({ kind: 'open', name, attrs, text: '', line: startLine, selfClosing });
    advance(end + 1);
  }
  return tokens;
}

function splitClasses(value: string | undefined): string[] {
  return (value ?? '').split(/\s+/).filter(Boolean);
}

/**
 * The id a layer gets when the file does not give it one.
 *
 * **Derived from where the layer sits, never from a counter.** A counter is
 * stable within one parse and useless across two, and the canvas re-parses on
 * every edit — so a monotonic id meant the selected layer's id changed the
 * instant anything was typed, and the selection died on every keystroke. It
 * also meant two parses of the same bytes disagreed about what a layer was
 * called, which is not a property an identifier may have.
 *
 * A position-derived id is stable for as long as the layer stays where it is,
 * which is exactly the guarantee a *provisional* id should make. The moment a
 * layer needs to survive being moved, it has earned a durable id in the file.
 */
function provisional(path: readonly (number | string)[], taken: ReadonlySet<string>): LayerId {
  const derived = `l_${path.join('_')}`;
  if (!taken.has(derived)) return derived;
  // A layer the file names explicitly has already claimed this string — which
  // happens the moment anything is *moved*, because the layer that carried an
  // anchor keeps its id while a different layer inherits its position. Without
  // this the two collide and the design stops parsing altogether, so one drag
  // on a design with a comment on it destroys the file.
  //
  // Determinism is preserved: the same bytes still produce the same ids,
  // because `taken` is a property of the bytes.
  for (let suffix = 1; ; suffix++) {
    const candidate = `${derived}b${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export function parseDesign(source: string): ParseResult {
  const errors: ParseError[] = [];
  const tokens = tokenize(source, errors);

  // Every id the file states outright, gathered before anything is assigned.
  // Positional ids are then chosen around them rather than into them.
  const claimed = new Set<string>();
  for (const token of tokens) {
    if (token.kind !== 'open' || !token.attrs.id) continue;
    // `$` separates a component instance from the path inside it, so an
    // authored id containing one would be read as a layer that does not exist
    // and the click that selected it would select nothing. The invariant is
    // load-bearing, so it is enforced here rather than assumed.
    if (token.attrs.id.includes('$')) {
      errors.push({
        line: token.line,
        message: `\`id="${token.attrs.id}"\` cannot contain \`$\`. That character marks a layer drawn by a component.`,
      });
      continue;
    }
    claimed.add(token.attrs.id);
  }

  let name = 'Untitled design';
  let seenDesign = false;
  const frames: Frame[] = [];
  const components: Component[] = [];

  // An explicit stack rather than recursion: the error messages need to name
  // the element that was left open, and a recursive descent loses that.
  const stack: { name: string; line: number; attrs: Record<string, string>; children: Layer[] }[] = [];

  /**
   * Where the element being closed sits, as a path of child indexes.
   *
   * Skips the `<design>` entry by *name* rather than by assuming it is at
   * index 0. It is not always there — a fragment with no wrapper is accepted
   * for the frames it contains — and assuming it was made a frame and its
   * first child both come out as `l_0`, which is a collision in an identifier.
   */
  const pathOf = (): number[] =>
    stack.filter((entry) => entry.name !== 'design').map((entry) => entry.children.length);

  /**
   * Where the layer being finished sits, for the id it is about to be given.
   *
   * Definitions are numbered in their own namespace — `l_c0_1` rather than
   * `l_0_1` — because a definition is not inside any frame, and reusing the
   * frame numbering would give a component's root the same id as the first
   * frame's first child. That collision makes a design unparseable, which is
   * the failure this codebase has already had once.
   */
  const idPath = (): (number | string)[] =>
    stack.some((entry) => entry.name === 'define')
      ? [`c${components.length}`, ...pathOf()]
      : [frames.length, ...pathOf()];

  const finishLayer = (
    frame: { name: string; attrs: Record<string, string>; children: Layer[]; line: number },
    path: readonly (number | string)[],
  ): Layer | null => {
    const attrs = frame.attrs;
    const base = {
      id: attrs.id || provisional(path, claimed),
      name: attrs.name || defaultLayerName(frame.name as 'box' | 'text' | 'image', frame.children.length),
      classes: splitClasses(attrs.class),
    };
    if (frame.name === 'box') return { ...base, kind: 'box', children: frame.children };
    if (frame.name === 'text') {
      return { ...base, kind: 'text', content: frame.children.length === 0 ? (attrs['#text'] ?? '') : '' };
    }
    if (frame.name === 'image') {
      if (!attrs.src) {
        errors.push({ line: frame.line, message: 'An image needs a `src`.' });
        return null;
      }
      return { ...base, kind: 'image', src: attrs.src, alt: attrs.alt ?? '' } satisfies ImageLayer;
    }
    if (frame.name === 'use') {
      const component = attrs.component ?? '';
      if (!component) {
        errors.push({ line: frame.line, message: 'A `<use>` needs a `component`, naming a `<define>`.' });
        return null;
      }
      // Everything that is not a structural attribute is a slot value. An open
      // set here is right where it is wrong elsewhere: the slot names come from
      // the definition, and the linter checks them against it — the parser
      // cannot, because the definition may be written after the use.
      const slots: Record<string, string> = Object.create(null) as Record<string, string>;
      for (const [key, value] of Object.entries(attrs)) {
        if (!USE_ATTRS.has(key) && !key.startsWith('#')) slots[key] = value;
      }
      return { ...base, name: attrs.name || component, kind: 'use', component, slots };
    }
    return null;
  };

  for (const token of tokens) {
    if (token.kind === 'text') {
      const top = stack[stack.length - 1];
      if (!top) {
        errors.push({ line: token.line, message: 'Words outside the design. Put them in a `<text>` inside a `<frame>`.' });
        continue;
      }
      if (top.name !== 'text') {
        errors.push({
          line: token.line,
          message: `Loose text inside \`<${top.name}>\`. Words belong in a \`<text>\` element.`,
        });
        continue;
      }
      top.attrs['#text'] = (top.attrs['#text'] ?? '') + token.text;
      continue;
    }

    if (token.kind === 'open') {
      if (!ELEMENTS.has(token.name)) {
        errors.push({
          line: token.line,
          message: `\`<${token.name}>\` is not an element this format has. The elements are ${[...ELEMENTS].map((e) => `\`<${e}>\``).join(', ')}.`,
        });
        continue;
      }
      const enclosing = stack[stack.length - 1];

      if (token.name === 'define') {
        if (enclosing?.name !== 'design') {
          errors.push({
            line: token.line,
            message: '`<define>` goes at the top of the design, not inside a frame. A definition is not drawn anywhere.',
          });
          continue;
        }
        if (!token.attrs.name) {
          errors.push({ line: token.line, message: 'A `<define>` needs a `name`, which is what a `<use>` refers to.' });
          continue;
        }
      }

      // Three nestings the grammar does not have. Each one used to be accepted
      // and quietly flattened — a nested frame was hoisted to the top level and
      // emitted *first*, so frames reordered on save.
      if (token.name === 'design' && enclosing) {
        errors.push({ line: token.line, message: '`<design>` cannot contain another `<design>`.' });
        continue;
      }
      if (token.name === 'frame' && enclosing && enclosing.name !== 'design') {
        errors.push({ line: token.line, message: `A \`<frame>\` cannot go inside \`<${enclosing.name}>\`.` });
        continue;
      }
      if (enclosing?.name === 'text') {
        errors.push({
          line: token.line,
          message: `\`<text>\` holds words, not a \`<${token.name}>\`. Put them side by side in a \`<box>\`.`,
        });
        continue;
      }

      if (token.name === 'design' && seenDesign) {
        errors.push({ line: token.line, message: 'A file holds one `<design>`.' });
        continue;
      }
      if (token.name === 'design') {
        seenDesign = true;
        name = token.attrs.name || name;
        stack.push({ name: 'design', line: token.line, attrs: token.attrs, children: [] });
        continue;
      }
      const entry = { name: token.name, line: token.line, attrs: token.attrs, children: [] as Layer[] };
      if (token.selfClosing || token.name === 'image' || token.name === 'use') {
        // `<image>` never has children, so it closes itself whether or not the
        // author wrote the slash.
        const layer = finishLayer(entry, idPath());
        const parent = stack[stack.length - 1];
        if (layer && parent) parent.children.push(layer);
        else if (layer) errors.push({ line: token.line, message: 'A layer outside any frame.' });
        continue;
      }
      stack.push(entry);
      continue;
    }

    // close
    // `<image>` never has children, so it is completed the moment it opens and
    // never reaches the stack. A written-out `</image>` would therefore pop its
    // *parent*, and the resulting cascade reported four errors, none of which
    // named the actual mistake.
    if (token.name === 'image' || token.name === 'use') continue;

    const top = stack.pop();
    if (!top) {
      errors.push({ line: token.line, message: `\`</${token.name}>\` closes nothing.` });
      continue;
    }
    if (top.name !== token.name) {
      errors.push({
        line: token.line,
        message: `\`</${token.name}>\` closes \`<${top.name}>\`, opened on line ${top.line}.`,
      });
    }
    if (top.name === 'design') continue;
    if (top.name === 'define') {
      if (top.children.length !== 1) {
        errors.push({
          line: top.line,
          message: `\`<define name="${top.attrs.name}">\` holds ${top.children.length} layers. A component has exactly one root — two roots is two components.`,
        });
        continue;
      }
      if (components.some((one) => one.name === top.attrs.name)) {
        errors.push({ line: top.line, message: `Two components are called \`${top.attrs.name}\`.` });
        continue;
      }
      components.push({ name: top.attrs.name!, layer: top.children[0]! });
      continue;
    }
    if (top.name === 'frame') {
      // Digits only. `Number()` swallows `0x10`, `1e3` and ` 10 ` and the file
      // was then rewritten with the coerced value — a save that silently
      // changed a number nobody touched.
      const rawWidth = top.attrs.width ?? '';
      const width = /^\d+$/.test(rawWidth) ? Number(rawWidth) : Number.NaN;
      if (!Number.isFinite(width) || width <= 0) {
        errors.push({ line: top.line, message: 'A frame needs a `width` in pixels.' });
        continue;
      }
      const rawHeight = top.attrs.height ?? 'auto';
      const height = rawHeight === 'auto' ? 'auto' : /^\d+$/.test(rawHeight) ? Number(rawHeight) : Number.NaN;
      // Checked the same way `width` is. It was not, so `height="tall"` became
      // `NaN`, was written back to the file as `height="NaN"`, and reached the
      // renderer as a style nobody could see was wrong.
      if (height !== 'auto' && (!Number.isFinite(height) || height <= 0)) {
        errors.push({ line: top.line, message: '`height` must be a number of pixels, or `auto`.' });
        continue;
      }
      frames.push({
        id: top.attrs.id || provisional([frames.length], claimed),
        name: top.attrs.name || defaultFrameName(frames.length),
        width,
        height,
        classes: splitClasses(top.attrs.class),
        children: top.children,
      });
      continue;
    }
    const layer = finishLayer(top, idPath());
    const parent = stack[stack.length - 1];
    if (!layer) continue;
    if (!parent || parent.name === 'design') {
      errors.push({ line: top.line, message: 'A layer outside any frame. Wrap it in a `<frame>`.' });
      continue;
    }
    parent.children.push(layer);
  }

  for (const unclosed of stack) {
    errors.push({ line: unclosed.line, message: `\`<${unclosed.name}>\` was never closed.` });
  }

  // An id names one layer. Two layers sharing one is caught by the linter as
  // well, but by then the tree has already been handed to a canvas that keys
  // React on it and matches edits by it — so selecting, editing or deleting
  // one of a colliding pair hit all of them.
  const seen = new Set<string>();
  const collide = (id: string, line: number): void => {
    if (seen.has(id)) errors.push({ line, message: `\`${id}\` names more than one layer.` });
    seen.add(id);
  };
  const visit = (layer: Layer): void => {
    collide(layer.id, 0);
    if (layer.kind === 'box') layer.children.forEach(visit);
  };
  for (const component of components) visit(component.layer);
  for (const frame of frames) {
    collide(frame.id, 0);
    frame.children.forEach(visit);
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    design: { name, frames, ...(components.length > 0 ? { components } : {}) },
  };
}
